import { useEffect, useState } from "react";
import {
  INITIATIVE_LIST_ID,
  LIBRARY_LIST_ID,
  PLATFORM_LIST_ID,
  fetchLatestAINewsDigest,
  fetchListCount,
  fetchRecentAcrossLists,
  type RecentItem,
} from "../api/graph";
import { useGraphToken } from "../auth/useGraphToken";
import type { AINewsDigest } from "../types/navigator";

export interface DashboardCounts {
  library: number;
  initiatives: number;
  platforms: number;
  approved: number;
  newThisMonth: number;
}

export interface DashboardState {
  counts: DashboardCounts | null;
  recent: RecentItem[];
  newsDigest: AINewsDigest | null;
  loadingCounts: boolean;
  loadingRecent: boolean;
  loadingNews: boolean;
  errorCounts: string | null;
  errorRecent: string | null;
  errorNews: string | null;
  /** Re-runs all three fetches — call after an edit elsewhere so the dashboard stays in sync. */
  refetch: () => void;
}

function firstOfMonthIso(): string {
  const d = new Date();
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

export function useDashboardData(): DashboardState {
  const getToken = useGraphToken();
  const [state, setState] = useState<Omit<DashboardState, "refetch">>({
    counts: null,
    recent: [],
    newsDigest: null,
    loadingCounts: true,
    loadingRecent: true,
    loadingNews: true,
    errorCounts: null,
    errorRecent: null,
    errorNews: null,
  });
  const [reloadKey, setReloadKey] = useState(0);
  const refetch = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loadingCounts: true, loadingRecent: true, loadingNews: true }));

    (async () => {
      try {
        const token = await getToken();

        // Kick off all three independently so a single slow/failing source
        // doesn't block the rest of the dashboard.
        const monthCutoff = firstOfMonthIso();

        // 1. Counts
        (async () => {
          try {
            const [lib, init, plat, approved, newThisMonth] = await Promise.all([
              fetchListCount(token, LIBRARY_LIST_ID).catch(() => 0),
              fetchListCount(token, INITIATIVE_LIST_ID).catch(() => 0),
              fetchListCount(token, PLATFORM_LIST_ID).catch(() => 0),
              fetchListCount(
                token,
                LIBRARY_LIST_ID,
                `fields/AssetStatus eq 'Published'`
              ).catch(() => 0),
              fetchListCount(
                token,
                LIBRARY_LIST_ID,
                `fields/Created ge '${monthCutoff}'`
              ).catch(() => 0),
            ]);
            if (cancelled) return;
            setState((s) => ({
              ...s,
              counts: {
                library: lib,
                initiatives: init,
                platforms: plat,
                approved,
                newThisMonth,
              },
              loadingCounts: false,
            }));
          } catch (e) {
            if (cancelled) return;
            setState((s) => ({
              ...s,
              errorCounts: e instanceof Error ? e.message : String(e),
              loadingCounts: false,
            }));
          }
        })();

        // 2. Recent across lists
        (async () => {
          try {
            const recent = await fetchRecentAcrossLists(token, 4);
            if (cancelled) return;
            setState((s) => ({ ...s, recent, loadingRecent: false }));
          } catch (e) {
            if (cancelled) return;
            setState((s) => ({
              ...s,
              errorRecent: e instanceof Error ? e.message : String(e),
              loadingRecent: false,
            }));
          }
        })();

        // 3. AI News — single latest digest row, with 5 section columns.
        (async () => {
          try {
            const newsDigest = await fetchLatestAINewsDigest(token);
            if (cancelled) return;
            setState((s) => ({ ...s, newsDigest, loadingNews: false }));
          } catch (e) {
            if (cancelled) return;
            setState((s) => ({
              ...s,
              errorNews: e instanceof Error ? e.message : String(e),
              loadingNews: false,
            }));
          }
        })();
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : String(e);
        setState((s) => ({
          ...s,
          loadingCounts: false,
          loadingRecent: false,
          loadingNews: false,
          errorCounts: msg,
          errorRecent: msg,
          errorNews: msg,
        }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [getToken, reloadKey]);

  return { ...state, refetch };
}
